/**
 * Slash matching ladder — PowerPoint specification ST-07 / §4.2.
 *
 * A typed slash resolves against the host's commands in a fixed preference
 * order: explicit shortcut, exact name, exact id, name substring, id substring
 * — so ``/cleanup`` reaches "Deal Memo Cleanup" without its shortcut being
 * learned. The first rung with a hit wins; within a rung the registry order
 * (Studio's sort order) decides, which is the same tie-break the composer
 * palette already applies when several shortcuts share a typed prefix.
 *
 * Names and ids are compared as slugs (lower-case, letters and digits only), so
 * ``/deal-memo-cleanup``, ``/deal_memo_cleanup`` and ``/dealmemocleanup`` all
 * name the same skill — a typed token stops at the first space, so the name
 * can never be typed with its spaces.
 *
 * Only skill commands take part beyond the first rung: local and discovery
 * commands carry an empty ``skillKey`` and are answered in the add-in, so they
 * match on their own shortcut alone.
 *
 * Scoped to PowerPoint: Word and Outlook keep exact-shortcut matching, so a
 * slash they refuse today (SAF-1) cannot start firing a skill there.
 */
import type { Channel } from "../../shared/host";
import type { SlashCommand } from "./types";

const LADDER_HOSTS: ReadonlySet<Channel> = new Set<Channel>(["powerpoint"]);

/**
 * Shortest typed slug the substring rungs accept. Below this, ``/a`` or ``/de``
 * would fire whichever skill happens to sort first — a guess, not a match.
 */
export const MIN_PARTIAL_MATCH_LENGTH = 3;

/** True when typed slashes on this host resolve through the ladder. */
export function usesMatchingLadder(host: Channel | null): boolean {
  return host !== null && LADDER_HOSTS.has(host);
}

function slugOf(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Every command the typed slash matches, best match first. Empty when nothing
 * matches. ``slash`` may be given with or without its leading "/".
 */
export function rankSlashMatches(commands: readonly SlashCommand[], slash: string): readonly SlashCommand[] {
  const typed = slash.trim().toLowerCase();
  const shortcut = typed.startsWith("/") ? typed : `/${typed}`;
  const key = slugOf(typed);
  const partial = key.length >= MIN_PARTIAL_MATCH_LENGTH;

  const rungs: ReadonlyArray<(cmd: SlashCommand) => boolean> = [
    (cmd) => cmd.slash === shortcut,
    (cmd) => !!key && !!cmd.skillKey && slugOf(cmd.label) === key,
    (cmd) => !!key && !!cmd.skillKey && slugOf(cmd.skillKey) === key,
    (cmd) => partial && !!cmd.skillKey && slugOf(cmd.label).includes(key),
    (cmd) => partial && !!cmd.skillKey && slugOf(cmd.skillKey).includes(key),
  ];

  const ranked: SlashCommand[] = [];
  for (const matches of rungs) {
    for (const cmd of commands) {
      if (matches(cmd) && !ranked.includes(cmd)) ranked.push(cmd);
    }
  }
  return ranked;
}
